import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Divider,
  Grid,
  Button,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  CreditCard as CreditCardIcon,
  ReceiptLong as FileTextIcon,
  Inventory2 as PackageIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Add as PlusIcon,
  Download as DownloadIcon,
  CalendarMonth as CalendarIcon,
  Storage as DatabaseIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import api from '../../utils/api';

const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);

const formatDate = (dateString) => {
  const d = dateString ? new Date(dateString) : new Date();
  return new Intl.DateTimeFormat('en-US', { year: 'numeric', month: 'long', day: 'numeric' }).format(d);
};

const BillingSettings = () => {
  const [expanded, setExpanded] = useState('subscription');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [useMock, setUseMock] = useState(true);

  const [subscription, setSubscription] = useState(null);
  const [availablePlans, setAvailablePlans] = useState([]);
  const [selectedPlanId, setSelectedPlanId] = useState('');
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [invoices, setInvoices] = useState([]);

  const loadAll = async () => {
    try {
      setLoading(true);
      setError('');
      // Placeholder: wire to real endpoints when available
      if (useMock) {
        const mockSub = {
          planId: 'pro',
          planName: 'Pro',
          amount: 49,
          interval: 'month',
          status: 'active',
          currentPeriodEnd: new Date().toISOString(),
        };
        const mockPlans = [
          { id: 'basic', name: 'Basic', price: 19, interval: 'month', features: ['100 orders/mo', 'Email support'] },
          { id: 'pro', name: 'Pro', price: 49, interval: 'month', features: ['Unlimited orders', 'Priority support', 'Advanced analytics'] },
          { id: 'team', name: 'Team', price: 99, interval: 'month', features: ['Multi-user', 'Role management', 'SLA support'] },
        ];
        const mockMethods = [
          { id: 'pm_1', brand: 'VISA', last4: '4242', expMonth: '12', expYear: '2030', isDefault: true },
          { id: 'pm_2', brand: 'MC', last4: '4444', expMonth: '08', expYear: '2028', isDefault: false },
        ];
        const mockInvoices = [
          { id: 'inv_1', date: new Date().toISOString(), number: '0001', amount: 4900, status: 'paid', downloadUrl: '#' },
        ];
        setSubscription(mockSub);
        setAvailablePlans(mockPlans);
        setSelectedPlanId(mockSub.planId);
        setPaymentMethods(mockMethods);
        setInvoices(mockInvoices);
      } else {
        // Example endpoints if wired on server
        // const sub = await api.get('/billing/subscription');
        // const methods = await api.get('/billing/payment-methods');
        // const inv = await api.get('/billing/invoices');
        // setSubscription(sub.data.data);
        // setAvailablePlans(sub.data.data.availablePlans || []);
        // setSelectedPlanId(sub.data.data.planId);
        // setPaymentMethods(methods.data.data || []);
        // setInvoices(inv.data.data || []);
      }
    } catch (e) {
      console.error(e);
      setError('Failed to load billing data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useMock]);

  const toggle = (k) => setExpanded((prev) => (prev === k ? null : k));

  const handleChangePlan = async () => {
    if (!selectedPlanId || !subscription) return;
    if (selectedPlanId === subscription.planId) return;
    try {
      setLoading(true);
      if (!useMock) {
        // await api.put('/billing/subscription', { planId: selectedPlanId });
      }
      setSubscription((s) => ({ ...s, planId: selectedPlanId, planName: (availablePlans.find(p => p.id === selectedPlanId) || {}).name }));
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">Billing & Subscription</Typography>
        <Button
          size="small"
          variant="outlined"
          startIcon={<DatabaseIcon />}
          onClick={() => setUseMock((v) => !v)}
        >
          {useMock ? 'Using Mock Data' : 'Using API Data'}
        </Button>
      </Box>

      {loading && (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Paper sx={{ p: 2, mb: 2, bgcolor: (t) => t.palette.error.main, color: '#fff' }}>
          {error}
        </Paper>
      )}

      {/* Subscription */}
      <Paper variant="outlined" sx={{ mb: 3 }}>
        <Box
          onClick={() => toggle('subscription')}
          sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <PackageIcon fontSize="small" color="action" />
            <Typography variant="subtitle1">Current Subscription</Typography>
          </Box>
          {expanded === 'subscription' ? <ExpandLessIcon color="action" /> : <ExpandMoreIcon color="action" />}
        </Box>
        {expanded === 'subscription' && (
          <Box sx={{ p: 2, pt: 0 }}>
            {subscription ? (
              <>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={7}>
                    <Typography variant="h6">{subscription.planName} Plan</Typography>
                    <Box mt={1}>
                      <Chip size="small" label={subscription.status === 'active' ? 'Active' : 'Inactive'} color={subscription.status === 'active' ? 'success' : 'warning'} />
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={5} textAlign={{ xs: 'left', md: 'right' }}>
                    <Typography variant="h6">{formatCurrency(subscription.amount)}/{subscription.interval}</Typography>
                    <Typography variant="body2" color="text.secondary">Next billing date: {formatDate(subscription.currentPeriodEnd)}</Typography>
                  </Grid>
                </Grid>

                <Box display="flex" justifyContent="flex-end" mt={2}>
                  <Button variant="contained" onClick={handleChangePlan} disabled={loading || selectedPlanId === subscription.planId}>
                    Confirm Change
                  </Button>
                </Box>

                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle1" gutterBottom>Select a new plan:</Typography>
                <Grid container spacing={2}>
                  {availablePlans.map((plan) => (
                    <Grid item xs={12} md={4} key={plan.id}>
                      <Paper
                        onClick={() => setSelectedPlanId(plan.id)}
                        variant={selectedPlanId === plan.id ? 'elevation' : 'outlined'}
                        elevation={selectedPlanId === plan.id ? 3 : 0}
                        sx={{ p: 2, cursor: 'pointer' }}
                      >
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Typography variant="subtitle1">{plan.name}</Typography>
                          {selectedPlanId === plan.id && <CheckCircleIcon color="primary" fontSize="small" />}
                        </Box>
                        <Typography variant="h6" mt={1}>{formatCurrency(plan.price)}/{plan.interval}</Typography>
                        <Box component="ul" sx={{ pl: 3, m: 0, mt: 1 }}>
                          {plan.features.map((f, i) => (
                            <li key={i}>
                              <Typography variant="body2" color="text.secondary">{f}</Typography>
                            </li>
                          ))}
                        </Box>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </>
            ) : (
              <Typography color="text.secondary">No subscription data available.</Typography>
            )}
          </Box>
        )}
      </Paper>

      {/* Payment Methods */}
      <Paper variant="outlined" sx={{ mb: 3 }}>
        <Box
          onClick={() => toggle('payment')}
          sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <CreditCardIcon fontSize="small" color="action" />
            <Typography variant="subtitle1">Payment Methods</Typography>
          </Box>
          {expanded === 'payment' ? <ExpandLessIcon color="action" /> : <ExpandMoreIcon color="action" />}
        </Box>
        {expanded === 'payment' && (
          <Box sx={{ p: 2, pt: 0 }}>
            {paymentMethods.length > 0 ? (
              <Grid container spacing={1}>
                {paymentMethods.map((m) => (
                  <Grid item xs={12} key={m.id}>
                    <Paper sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box display="flex" alignItems="center" gap={2}>
                        <Box sx={{ width: 48, height: 28, bgcolor: 'action.hover', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <Typography variant="caption" fontWeight={700}>{m.brand}</Typography>
                        </Box>
                        <Box>
                          <Typography>•••• {m.last4} {m.isDefault && (
                            <Chip size="small" label="Default" color="primary" sx={{ ml: 1 }} />
                          )}</Typography>
                          <Typography variant="caption" color="text.secondary">Expires {m.expMonth}/{m.expYear}</Typography>
                        </Box>
                      </Box>
                      <Box display="flex" gap={1}>
                        {!m.isDefault && (
                          <Button size="small" variant="outlined">Set as Default</Button>
                        )}
                        <Button size="small" color="error" variant="outlined">Remove</Button>
                      </Box>
                    </Paper>
                  </Grid>
                ))}
                <Grid item xs={12} textAlign="right">
                  <Button startIcon={<PlusIcon />} variant="outlined">Add Payment Method</Button>
                </Grid>
              </Grid>
            ) : (
              <Box textAlign="center" py={2}>
                <Typography color="text.secondary">No payment methods available.</Typography>
                <Button sx={{ mt: 1 }} startIcon={<PlusIcon />} variant="outlined">Add Payment Method</Button>
              </Box>
            )}
          </Box>
        )}
      </Paper>

      {/* Billing History */}
      <Paper variant="outlined" sx={{ mb: 3 }}>
        <Box
          onClick={() => toggle('history')}
          sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <FileTextIcon fontSize="small" color="action" />
            <Typography variant="subtitle1">Billing History</Typography>
          </Box>
          {expanded === 'history' ? <ExpandLessIcon color="action" /> : <ExpandMoreIcon color="action" />}
        </Box>
        {expanded === 'history' && (
          <Box sx={{ p: 2, pt: 0 }}>
            {invoices.length > 0 ? (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Invoice #</TableCell>
                    <TableCell>Amount</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Download</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {invoices.map((inv) => (
                    <TableRow key={inv.id} hover>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <CalendarIcon fontSize="inherit" color="action" />
                          {formatDate(inv.date)}
                        </Box>
                      </TableCell>
                      <TableCell>{inv.number}</TableCell>
                      <TableCell>{formatCurrency(inv.amount / 100)}</TableCell>
                      <TableCell>
                        <Chip size="small" label={inv.status} color={inv.status === 'paid' ? 'success' : 'warning'} />
                      </TableCell>
                      <TableCell align="right">
                        <Button size="small" startIcon={<DownloadIcon />} component="a" href={inv.downloadUrl} target="_blank" rel="noopener noreferrer">
                          PDF
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <Typography color="text.secondary">No billing history available.</Typography>
            )}
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default BillingSettings;
